import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  useDrawer,
  useBreakpoint
} from '@cat-ecom/pcc-components';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { CatHeading } from 'blocks-nextjs/blocks-react/components/Heading';
import { CatIconArrowBack } from 'blocks-nextjs/blocks-react/components/Icons/ArrowBack';
import { useTranslations } from 'next-intl';
import { FC, PropsWithChildren } from 'react';
import { ViewResultsButton } from './common/view-results-button';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { usePathname } from 'next/navigation';
import { ClearFiltersButton } from './common/clear-filters-button';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { STATUS_FAIL, STATUS_SUCCESS } from 'constants/analyticsConstants';
import { useFormContext } from 'react-hook-form';

type NotificationsFilterDrawerProps = {
  drawer: ReturnType<typeof useDrawer>;
  notificationsCount: number;
  showClearAllFiltersButton: string | number;
  handleClearAllFilters: () => void;
};

export const NotificationsFilterDrawer: FC<
  PropsWithChildren<NotificationsFilterDrawerProps>
> = ({
  drawer,
  notificationsCount,
  showClearAllFiltersButton,
  handleClearAllFilters,
  children
}) => {
  const { setValue, watch } = useFormContext();
  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { fireEvent } = useFormSubmitEvent();
  const pathname = usePathname();
  const updatedPath = `/${pathname.split('/').splice(1, 2).join('/')}/`;
  const hasPOCharacterError = watch('hasPOCharacterError');
  const hasOrderOrReferenceNumberFieldError = watch(
    'hasOrderOrReferenceNumberFieldError'
  );
  const formStatus = notificationsCount > 0 ? STATUS_SUCCESS : STATUS_FAIL;

  const submitFireEvent = () =>
    fireEvent({
      pagePath: updatedPath,
      formName: 'Notification Preferences Drawer Filter',
      formContent: 'Order/Quote reference Number',
      formContent2: 'Po Number: testP',
      formStatus: formStatus,
      formFieldCausingError:
        formStatus === STATUS_FAIL ? 'Invalid field(s) causing error' : '',
      formLocation: 'Notification Preferences'
    });

  const validateAndClearFieldsOnError = (): void => {
    if (hasPOCharacterError) {
      setValue('hasPOCharacterError', false);
      setValue('filterByPONumber', '');
    }

    if (hasOrderOrReferenceNumberFieldError) {
      setValue('hasOrderOrReferenceNumberFieldError', false);
      setValue('filterByOrderOrReferenceNumber', '');
    }
  };

  const closeDrawer = () => {
    submitFireEvent();

    validateAndClearFieldsOnError();

    drawer.closeDrawer();
  };

  const closeBlDrawer = (e) => {
    submitFireEvent();

    validateAndClearFieldsOnError();

    drawer.drawerProps.onBlDrawerClose(e);
  };
  return (
    <Drawer {...drawer.drawerProps} onBlDrawerClose={closeBlDrawer}>
      <DrawerHeader className={'d-flex align-items-center text-capitalize'}>
        <CatButton
          className="me-2"
          hideText
          onClick={closeDrawer}
          variant="ghost"
        >
          <CatIconArrowBack slot="before" />
        </CatButton>
        <CatHeading
          variant="title"
          slot="header"
          className="cat-u-theme-typography-title-sm"
        >
          {t('FILTER_OR_SEARCH')}
        </CatHeading>
      </DrawerHeader>
      <DrawerBody className="p-3 ps-4">{children}</DrawerBody>
      <DrawerFooter>
        <ViewResultsButton
          onClick={closeDrawer}
          count={notificationsCount}
          fullWidth={!isMD}
        />
        {showClearAllFiltersButton && (
          <ClearFiltersButton
            onClick={handleClearAllFilters}
            fullWidth={!isMD}
            showClearAllFiltersButton={showClearAllFiltersButton}
          />
        )}
      </DrawerFooter>
    </Drawer>
  );
};
